// Modules
import React, { useState } from 'react'
import { ErrorResponse } from 'apollo-link-error'
import { useTracking } from 'react-tracking'
import { useHistory } from 'react-router-dom'

// Applications
import { useApp } from 'context'
import { useAccountMDMHook, useActivateHook, useLoginHook } from 'hooks'
import { ContactType, StatusMessage } from 'typings'
import { OutputErrorCode, OutputErrorMsg, ROUTES_PATH, tracking } from 'constants/index'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { clearStorage, singleSPA } from 'utils'

// Components
import { ValidateTokenInitial } from 'components/Presenters/ValidateToken'
import { activateTrack } from 'pages/ActivateAccount/analytics'
import { ResendTokenType } from 'typings/account'
import { ValidateDestinationToken } from 'components/Presenters/ValidateDestinationToken'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'

const NAV_LS_KEY = '@NavAuth'

export const ValidateTokenWrapper: React.VFC = () => {
    const { setSubmitting, confirmToken, resetAccountState, cpf, password, contact, isRecoverPassword } = useActivateHook()
    const { resendToken } = useAccountMDMHook()
    const { phasedLoginVisibility } = usePhasedLogin()
    const { openToast } = useApp()
    const history = useHistory()

    const { trackEvent } = useTracking()
    const { verifyUser } = useLoginHook()

    const [isLoading, setLoading] = useState<boolean>(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })

    const handleSubmit = async (values: ValidateTokenInitial) => {
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')
        try {
            setSubmitting(true)
            const loginSuccessfully = await confirmToken(token!)

            setSubmitting(false)
            setFieldState({ status: 'success', message: 'Código validado' })
            clearStorage([`${NAV_LS_KEY}:Contact`])

            const passwordRecoverTrackValues = {
                externalStep: phasedLoginVisibility ? 'PHASED_LOGIN' : 'LOGIN',
                loginType: tracking.LOGIN_TYPE.RESET,
            }

            trackEvent(activateTrack(!!isRecoverPassword, contact?.contactType))

            if (loginSuccessfully) {
                await verifyUser(passwordRecoverTrackValues)

                setTimeout(() => {
                    singleSPA.historyPush(defaultConfig.strings.AUTH_REDIRECT_LOGIN, {
                        status: 'success',
                        message: 'Cadastro realizado com sucesso!',
                    })
                    resetAccountState()
                }, 600)
            } else {
                setTimeout(() => {
                    history.push(ROUTES_PATH.LOGIN, { status: 'success', message: 'Cadastro realizado com sucesso!' })
                    resetAccountState()
                }, 3600)
            }
        } catch (err) {
            setSubmitting(false)
            const e: ErrorResponse = err
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (codeError === OutputErrorCode.INVALIDTOKEN || codeError === OutputErrorCode.EXPIREDTOKEN) {
                    setFieldState({
                        status: 'error',
                        message:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    return
                }
            }
            setFieldState({ status: '', message: '' })
            openToast('Ocorreu um erro')
        }
    }

    const handleResendTokenModal = async (contactType: ContactType) => {
        try {
            setLoading(true)
            const patientLogin: ResendTokenType = {
                contactId: contact?.id,
                cpf: cpf,
                password: password,
                destination: contactType,
            }
            await resendToken(patientLogin)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            openToast('Ocorreu um erro')
        }
    }

    return (
        <ValidateDestinationToken
            fieldState={fieldState}
            handleResendToken={handleResendTokenModal}
            handleSubmit={handleSubmit}
            isDestinationMobile={contact?.contactType !== 'EMAIL'}
            isLoading={isLoading}
        />
    )
}
